import { UploadOutlined, FileOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Empty, Flex, Popconfirm, Typography, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { Fragment, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getAdditionalWorkingTimeTicketFile,
  updateAdditionalWorkingTimeTicket,
  uploadAdditionalWorkingTimeTicketFile
} from 'src/apis/hr_ticket/additionalWorkingTimeTicket.api';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { showNotification } from 'src/utils/notification';

const { Text } = Typography;

const MAX_FILE_SIZE_MB = 15;
const FILE_ACCEPTS = 'image/*,.txt,.doc,.docx,.xlsx,.pdf,.csv';

export default function AdditionalWorkingTimeAttachment() {
  const { additionalWorkingTimeTicket } = useContext(HrTicketDetailContext);
  const { userInfo } = useContext(AppContext);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const ticketData = additionalWorkingTimeTicket?.data;
  const url = ticketData?.url;
  const isOwner = ticketData?.createdById === userInfo?.id;
  const canEdit = isOwner && ticketData?.isAvailableEdit;

  // Extract filename from URL
  const getFileName = (url: string) => {
    try {
      const parts = url.split('/');
      return decodeURIComponent(parts[parts.length - 1]) || 'file';
    } catch {
      return 'file';
    }
  };

  // Upload file -> get URL -> update ticket with URL
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAdditionalWorkingTimeTicketFile([file])
  });

  const updateTicketMutation = useMutation({
    mutationFn: (newAttachmentUrl: string | null) =>
      updateAdditionalWorkingTimeTicket(Number(id), {
        note: ticketData?.note,
        additionalWorkingTimePackageId: ticketData?.additionalWorkingTimePackageId ?? null,
        additionalWorkingTimeTicketDetails:
          ticketData?.additionalWorkingTimeTicketDetails?.map((item) => ({
            id: item.id,
            additionalTypeId: item.additionalTypeId,
            employeeId: item.employeeId,
            branchId: item.branchId,
            date: item.date,
            startTime: item.startTime as string,
            endTime: item.endTime as string,
            note: item.note,
            refTicketId: item.refTicketId
          })) || [],
        additionalWorkingTimeTicketRefs: ticketData?.additionalWorkingTimeTicketRefs || [],
        url: newAttachmentUrl
      })
  });

  const handleUpload = (file: RcFile) => {
    const isOverSize = file.size / (1024 * 1024) > MAX_FILE_SIZE_MB;
    if (isOverSize) {
      message.warning(`File vượt quá ${MAX_FILE_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    if (file.size <= 0) {
      message.warning('File rỗng');
      return Upload.LIST_IGNORE;
    }

    setUploading(true);
    // Truyền trực tiếp file vào mutation
    uploadMutation.mutate(file as unknown as File, {
      onSuccess: (response) => {
        const newUrl = response.data;
        updateTicketMutation.mutate(newUrl, {
          onSuccess: () => {
            showNotification('Tải lên tệp đính kèm thành công!', 'success');
            queryClient.invalidateQueries(['additionalWorkingTimeTicket', id]);
            setUploading(false);
          },
          onError: () => {
            message.error('Lỗi khi cập nhật phiếu');
            setUploading(false);
          }
        });
      },
      onError: () => {
        message.error('Lỗi khi tải lên tệp');
        setUploading(false);
      }
    });

    return Upload.LIST_IGNORE;
  };

  // Delete attachment (set URL to null)
  const handleDeleteAttachment = () => {
    updateTicketMutation.mutate(null, {
      onSuccess: () => {
        showNotification('Xoá tệp đính kèm thành công!', 'success');
        queryClient.invalidateQueries(['additionalWorkingTimeTicket', id]);
      },
      onError: () => {
        message.error('Lỗi khi xoá tệp đính kèm');
      }
    });
  };

  const handleFileAction = async (action: 'view' | 'download') => {
    if (!url) return;
    try {
      const response = await getAdditionalWorkingTimeTicketFile(url);
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);

      if (action === 'view') {
        window.open(blobUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', getFileName(url));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Giải phóng bộ nhớ sau một khoảng thời gian ngắn
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      message.error('Không thể tải tệp');
    }
  };

  return (
    <Fragment>
      <Flex gap={10} vertical>
        <Divider style={{ marginBlock: 0 }} orientation='left'>
          <Text strong>Tệp đính kèm</Text>
        </Divider>

        {url ? (
          <Flex align='center' gap={8} wrap='wrap'>
            <FileOutlined style={{ fontSize: 16, color: '#1890ff' }} />
            <Text
              style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
              onClick={() => handleFileAction('download')}
            >
              {getFileName(url)}
            </Text>
            <Button type='link' size='small' icon={<DownloadOutlined />} onClick={() => handleFileAction('download')}>
              Tải xuống
            </Button>
            {canEdit && (
              <Popconfirm title='Bạn chắc chắn muốn xoá tệp đính kèm?' onConfirm={handleDeleteAttachment}>
                <Button
                  type='link'
                  size='small'
                  danger
                  icon={<DeleteOutlined />}
                  loading={updateTicketMutation.isLoading}
                >
                  Xoá
                </Button>
              </Popconfirm>
            )}
          </Flex>
        ) : (
          <Empty
            image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
            imageStyle={{ height: 80 }}
            description={<Text type='secondary'>Không có tệp đính kèm</Text>}
          />
        )}

        {canEdit && (
          <Upload accept={FILE_ACCEPTS} showUploadList={false} beforeUpload={handleUpload} maxCount={1}>
            <Button shape='default' type='link' size='middle' icon={<UploadOutlined />} loading={uploading}>
              {url ? 'Thay đổi tệp đính kèm' : 'Thêm đính kèm'}
            </Button>
          </Upload>
        )}
      </Flex>
    </Fragment>
  );
}
